/* =============================================================================
   Ehva cart drawer
   - Handles upsell add-to-cart (AJAX)
   - Loads Shopify recommendations when source is 'recommendations'
   - Refreshes the cart drawer in place after an upsell ATC
   ============================================================================= */

(function () {
  'use strict';

  const SELECTORS = {
    drawer: 'cart-drawer',
    drawerInner: 'cart-drawer .drawer__inner',
    upsell: '[data-ehva-upsell]',
    upsellScroller: '[data-ehva-upsell-scroller]',
    upsellAdd: '[data-ehva-upsell-add]',
    upsellPlaceholder: '[data-ehva-upsell-placeholder]',
  };

  /* --------------------------------------------------------------------------
     Upsell — handle "+ Add" clicks (event delegation, works after re-renders)
     -------------------------------------------------------------------------- */
  document.addEventListener('click', async function (event) {
    const btn = event.target.closest(SELECTORS.upsellAdd);
    if (!btn) return;

    event.preventDefault();
    if (btn.disabled || btn.classList.contains('is-adding')) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const addingText = btn.dataset.addingText || 'Adding…';
    const addedText = btn.dataset.addedText || '✓ Added';
    const originalText = btn.dataset.addText || btn.textContent.trim();

    btn.classList.add('is-adding');
    btn.textContent = addingText;
    btn.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) throw new Error('Add to cart failed');

      btn.classList.remove('is-adding');
      btn.classList.add('is-added');
      btn.textContent = addedText;

      await refreshCartDrawer();

      // Reset after refresh (drawer rebuilt, but in case button persists)
      setTimeout(() => {
        const persisted = document.querySelector(
          `[data-ehva-upsell-add][data-variant-id="${variantId}"]`
        );
        if (persisted) {
          persisted.classList.remove('is-added');
          persisted.textContent = originalText;
          persisted.disabled = false;
        }
      }, 1600);
    } catch (err) {
      console.error('Ehva upsell ATC error:', err);
      btn.classList.remove('is-adding');
      btn.textContent = 'Try again';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1600);
    }
  });

  /* --------------------------------------------------------------------------
     Refresh the cart drawer in place by re-fetching the section
     -------------------------------------------------------------------------- */
  async function refreshCartDrawer() {
    try {
      const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();

      const drawer = document.querySelector(SELECTORS.drawer);
      const drawerInner = document.querySelector(SELECTORS.drawerInner);

      if (drawer && drawerInner && sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(
          sections['cart-drawer'],
          'text/html'
        );
        const newInner = parsed.querySelector('.drawer__inner');
        if (newInner) {
          drawerInner.innerHTML = newInner.innerHTML;
        }
        drawer.classList.remove('is-empty');
      }

      // Update cart icon bubble
      const bubble = document.querySelector('#cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(
          sections['cart-icon-bubble'],
          'text/html'
        );
        const newBubble = parsed.querySelector('.shopify-section');
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      }

      // Open the drawer if it isn't already open
      if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') {
        drawer.open();
      }

      // Re-init recommendations (in case they got wiped)
      initRecommendations();
    } catch (err) {
      console.error('Ehva refreshCartDrawer error:', err);
    }
  }

  /* --------------------------------------------------------------------------
     Recommendations — fetch Shopify's auto product-recommendations endpoint
     -------------------------------------------------------------------------- */
  async function initRecommendations() {
    const upsell = document.querySelector(SELECTORS.upsell);
    if (!upsell) return;
    if (upsell.dataset.source !== 'recommendations') return;
    if (upsell.dataset.loaded === 'true') return;

    const productId = upsell.dataset.productId;
    const limit = parseInt(upsell.dataset.limit, 10) || 6;

    if (!productId) {
      // empty cart or no product → hide upsell silently
      upsell.style.display = 'none';
      return;
    }

    try {
      const url = `/recommendations/products?product_id=${productId}&limit=${limit}&section_id=cart-drawer-recommendations`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendations fetch failed');

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Try to find product items inside the response. The
      // section uses .product-card-wrapper, .grid__item, or .product items.
      const productNodes =
        parsed.querySelectorAll('product-recommendations [data-ehva-upsell-card]').length > 0
          ? parsed.querySelectorAll('product-recommendations [data-ehva-upsell-card]')
          : parsed.querySelectorAll('.product-card-wrapper, .grid__item, .card-wrapper');

      const scroller = upsell.querySelector(SELECTORS.upsellScroller);
      const placeholder = upsell.querySelector(SELECTORS.upsellPlaceholder);

      if (!productNodes.length) {
        upsell.style.display = 'none';
        return;
      }

      const cards = await fetchRecommendationCards(productId, limit);
      if (cards) {
        if (placeholder) placeholder.remove();
        scroller.innerHTML = cards;
        upsell.dataset.loaded = 'true';
      } else {
        upsell.style.display = 'none';
      }
    } catch (err) {
      console.error('Ehva recommendations error:', err);
      upsell.style.display = 'none';
    }
  }

  /* Fetch individual products by id (one-by-one is slow; use the
     recommendations JSON endpoint instead). */
  async function fetchRecommendationCards(productId, limit) {
    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.products || !data.products.length) return null;

      return data.products
        .map((p) => buildUpsellCardHTML(p))
        .join('');
    } catch (err) {
      console.error('Ehva recommendations JSON error:', err);
      return null;
    }
  }

  function buildUpsellCardHTML(product) {
    const variant = product.variants && product.variants[0];
    if (!variant) return '';
    const onSale = product.compare_at_price_min > product.price_min;
    const price = formatMoney(product.price_min);
    const compare = onSale ? formatMoney(product.compare_at_price_min) : '';
    const img = product.featured_image
      ? `<img src="${product.featured_image}&width=240" alt="${escapeHtml(product.title)}" class="ehva-upsell__image" loading="lazy" width="120" height="120">`
      : '<div class="ehva-upsell__image-placeholder"></div>';

    const addText = window.ehvaUpsellLabels?.add || '+ Add';
    const addingText = window.ehvaUpsellLabels?.adding || 'Adding…';
    const addedText = window.ehvaUpsellLabels?.added || '✓ Added';

    return `
      <div class="ehva-upsell__card" data-ehva-upsell-card>
        <a href="${product.url}" class="ehva-upsell__image-wrap" tabindex="-1" aria-label="${escapeHtml(product.title)}">
          ${img}
        </a>
        <a href="${product.url}" class="ehva-upsell__title">${escapeHtml(product.title)}</a>
        <div class="ehva-upsell__row">
          <div class="ehva-upsell__price-wrap">
            <span class="ehva-upsell__price">${price}</span>
            ${onSale ? `<s class="ehva-upsell__compare">${compare}</s>` : ''}
          </div>
          <button
            type="button"
            class="ehva-upsell__add"
            data-ehva-upsell-add
            data-variant-id="${variant.id}"
            data-add-text="${escapeHtml(addText)}"
            data-adding-text="${escapeHtml(addingText)}"
            data-added-text="${escapeHtml(addedText)}"
            ${variant.available ? '' : 'disabled'}
          >${escapeHtml(addText)}</button>
        </div>
      </div>
    `;
  }

  function formatMoney(amount) {
    // amount is in cents
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(amount, window.Shopify.money_format);
    }
    const rupees = (amount / 100).toFixed(2).replace(/\.00$/, '');
    return `₹${rupees}`;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* --------------------------------------------------------------------------
     Observe cart-drawer for re-renders to re-init recommendations
     -------------------------------------------------------------------------- */
  function observeCartDrawer() {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    const observer = new MutationObserver(() => {
      // Drawer was re-rendered (cart updated); reset and re-init recs.
      initRecommendations();
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------------- */
  function boot() {
    initRecommendations();
    observeCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
